import { getAudioContext } from './audio-engine';

// Singleton player + loader instances (WebAudioFontPlayer loaded via CDN script tag)
let player: any = null;
let loader: any = null;

function ensurePlayer(): boolean {
  if (player) return true;
  if (typeof (window as any).WebAudioFontPlayer === 'undefined') return false;
  player = new (window as any).WebAudioFontPlayer();
  loader = player.loader;
  return true;
}

// Curated GM instruments (0-indexed program numbers)
export const CURATED_INSTRUMENTS: { program: number; name: string }[] = [
  // Piano
  { program: 0, name: 'Acoustic Grand Piano' },
  { program: 1, name: 'Bright Acoustic Piano' },
  { program: 4, name: 'Electric Piano (Rhodes)' },
  { program: 5, name: 'Electric Piano (DX)' },
  // Chromatic Percussion
  { program: 8, name: 'Celesta' },
  { program: 9, name: 'Glockenspiel' },
  { program: 10, name: 'Music Box' },
  { program: 11, name: 'Vibraphone' },
  { program: 12, name: 'Marimba' },
  { program: 13, name: 'Xylophone' },
  { program: 14, name: 'Tubular Bells' },
  // Guitar
  { program: 24, name: 'Nylon Guitar' },
  { program: 25, name: 'Steel Guitar' },
  { program: 26, name: 'Jazz Electric Guitar' },
  // Strings
  { program: 40, name: 'Violin' },
  { program: 42, name: 'Cello' },
  { program: 46, name: 'Harp' },
  { program: 48, name: 'String Ensemble' },
  // Brass
  { program: 56, name: 'Trumpet' },
  { program: 60, name: 'French Horn' },
  // Woodwind
  { program: 65, name: 'Alto Sax' },
  { program: 73, name: 'Flute' },
  { program: 74, name: 'Recorder' },
  { program: 79, name: 'Ocarina' },
  // Synth
  { program: 80, name: 'Square Lead' },
  { program: 88, name: 'New Age Pad' },
  // Ethnic
  { program: 104, name: 'Sitar' },
  { program: 108, name: 'Kalimba' },
  { program: 112, name: 'Tinkle Bell' },
];

// Cache of loaded presets keyed by program number
const presetCache = new Map<number, any>();

// In-flight load promises to avoid duplicate fetches
const loadPromises = new Map<number, Promise<void>>();

function instrumentKey(program: number): string {
  return `${String(program).padStart(3, '0')}0_FluidR3_GM_sf2_file`;
}

function instrumentVariable(program: number): string {
  return `_tone_${instrumentKey(program)}`;
}

function instrumentUrl(program: number): string {
  return `soundfonts/${instrumentKey(program)}.js`;
}

export function loadInstrument(gmProgram: number): Promise<void> {
  if (presetCache.has(gmProgram)) return Promise.resolve();

  const existing = loadPromises.get(gmProgram);
  if (existing) return existing;

  if (!ensurePlayer()) return Promise.resolve();
  const ctx = getAudioContext();
  const varName = instrumentVariable(gmProgram);
  const url = instrumentUrl(gmProgram);

  const promise = new Promise<void>((resolve) => {
    loader.startLoad(ctx, url, varName);
    loader.waitOrFinish(varName, () => {
      const preset = (window as any)[varName];
      if (preset) {
        presetCache.set(gmProgram, preset);
      }
      loadPromises.delete(gmProgram);
      resolve();
    });
  });

  loadPromises.set(gmProgram, promise);
  return promise;
}

export function isInstrumentLoaded(gmProgram: number): boolean {
  return presetCache.has(gmProgram);
}

let currentProgram = 0;

export function setCurrentProgram(n: number) {
  currentProgram = n;
}

export function getCurrentProgram(): number {
  return currentProgram;
}

export function freqToMidi(freq: number): number {
  return Math.round(12 * Math.log2(freq / 440) + 69);
}

export function playSoundfontNote(
  target: AudioNode,
  pitch: number,
  duration: number,
  volume = 0.5,
): any {
  if (!ensurePlayer()) return null;
  const preset = presetCache.get(currentProgram);
  if (!preset) return null;
  const ctx = getAudioContext();
  return player.queueWaveTable(ctx, target, preset, ctx.currentTime, pitch, duration, volume);
}

export function playSoundfontChord(
  target: AudioNode,
  pitches: number[],
  duration: number,
  volume = 0.5,
): void {
  for (const pitch of pitches) {
    playSoundfontNote(target, pitch, duration, volume);
  }
}
